/** Excel's own range: 1 January 1900 to 31 December 9999. */
const MIN_YEAR = 1900;
const MAX_YEAR = 9999;
const ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Whole days from 1970-01-01 to the given civil date (proleptic Gregorian calendar). */
export function daysFromCivil(year, month, day) {
    const y = month <= 2 ? year - 1 : year;
    const era = Math.floor(y / 400);
    const yearOfEra = y - era * 400;
    const dayOfYear = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
    const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear;
    return era * 146097 + dayOfEra - 719468;
}
/** The civil date that lies the given number of whole days after 1970-01-01. */
export function civilFromDays(days) {
    const z = days + 719468;
    const era = Math.floor(z / 146097);
    const dayOfEra = z - era * 146097;
    const yearOfEra = Math.floor((dayOfEra -
        Math.floor(dayOfEra / 1460) +
        Math.floor(dayOfEra / 36524) -
        Math.floor(dayOfEra / 146096)) /
        365);
    const dayOfYear = dayOfEra - (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100));
    const monthIndex = Math.floor((5 * dayOfYear + 2) / 153);
    const day = dayOfYear - Math.floor((153 * monthIndex + 2) / 5) + 1;
    const month = monthIndex < 10 ? monthIndex + 3 : monthIndex - 9;
    const year = yearOfEra + era * 400 + (month <= 2 ? 1 : 0);
    return { year, month, day };
}
function daysInMonth(year, month) {
    if (month === 2) {
        const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        return leap ? 29 : 28;
    }
    return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}
/** Read `'YYYY-MM-DD'`. `null` when the text is not a real calendar date. */
export function parseIsoDate(text) {
    if (typeof text !== 'string')
        return null;
    const found = ISO_PATTERN.exec(text);
    if (found === null)
        return null;
    const year = Number(found[1]);
    const month = Number(found[2]);
    const day = Number(found[3]);
    if (month < 1 || month > 12)
        return null;
    if (day < 1 || day > daysInMonth(year, month))
        return null;
    return { year, month, day };
}
function pad(value, width) {
    return String(value).padStart(width, '0');
}
/** Write a civil date as `'YYYY-MM-DD'`. */
export function toIsoDate(date) {
    return `${pad(date.year, 4)}-${pad(date.month, 2)}-${pad(date.day, 2)}`;
}
/** Whole days since 1970-01-01, or `null` for a text that is not a date. */
export function isoToDays(text) {
    const date = parseIsoDate(text);
    if (date === null)
        return null;
    return daysFromCivil(date.year, date.month, date.day);
}
/** The date for a day count, or `null` outside Excel's range (1900 to 9999). */
export function daysToIso(days) {
    if (!Number.isFinite(days))
        return null;
    const date = civilFromDays(Math.trunc(days));
    if (date.year < MIN_YEAR || date.year > MAX_YEAR)
        return null;
    return toIsoDate(date);
}
/** Excel `MONTH(date)`: 1 to 12. `null` for a text that is not a date. */
export function monthOf(text) {
    const date = parseIsoDate(text);
    return date === null ? null : date.month;
}
/** Excel `YEAR(date)`. `null` for a text that is not a date. */
export function yearOf(text) {
    const date = parseIsoDate(text);
    return date === null ? null : date.year;
}
/**
 * Excel `DATE(year, month, day)`. Fractions are cut off. A month outside 1 to 12 rolls the year
 * (14 is February of the next year, 0 is December of the year before), and a day outside the
 * month rolls the month.
 *
 * `null` for a year outside 1900 to 9999 and for a result outside that range. (Excel reads a
 * year under 1900 as 1900 plus that year and LibreOffice gives an error; the engine never asks
 * for one, because the budget year of a snapshot is validated.)
 */
export function excelDate(year, month, day) {
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day))
        return null;
    const y = Math.trunc(year);
    const m = Math.trunc(month);
    const d = Math.trunc(day);
    if (y < MIN_YEAR || y > MAX_YEAR)
        return null;
    // Far outside the range the result is an error anyway; this also keeps the sums exact.
    if (Math.abs(m) > 200000 || Math.abs(d) > 5000000)
        return null;
    const monthsFromJanuary = m - 1;
    const rolledYear = y + Math.floor(monthsFromJanuary / 12);
    const rolledMonth = monthsFromJanuary - 12 * Math.floor(monthsFromJanuary / 12) + 1;
    return daysToIso(daysFromCivil(rolledYear, rolledMonth, 1) + (d - 1));
}
const MONTHS_IN_YEAR = 12;
/**
 * The linear month index: `year × 12 + (month − 1)`, so January of year 0 is 0 and every next
 * month is one more. December to January is addition, never a wrap: `monthIndex(2026, 12) + 1`
 * is `monthIndex(2027, 1)`.
 *
 * The month is not held to 1 to 12 and need not be whole: month 13 is January of the next year
 * and month 0 is December of the year before, as in Excel's `DATE`.
 */
export function monthIndex(year, month) {
    return year * MONTHS_IN_YEAR + (month - 1);
}
/** The calendar month of a whole linear month index: the reverse of `monthIndex`. */
export function yearMonthOf(index) {
    const year = Math.floor(index / MONTHS_IN_YEAR);
    return { year, month: index - year * MONTHS_IN_YEAR + 1 };
}
/** Monday is 0, Sunday is 6. 1970-01-01 was a Thursday. */
function weekdayIndex(days) {
    return (((days + 3) % 7) + 7) % 7;
}
/** Saturday or Sunday, for a day number. */
export function isWeekend(days) {
    return weekdayIndex(days) >= 5;
}
/** Beyond this many working days the result is outside Excel's dates in any case. */
const MAX_WORKDAYS = 3000000;
/**
 * Excel `WORKDAY(date, n)` without a holiday list: the date `n` working days (Monday to Friday)
 * after `date`, or before it when `n` is negative. `workday(d, 1)` is the next working day, so a
 * Friday, a Saturday and a Sunday all give the Monday. `n = 0` gives the date itself, weekend or
 * not. A fraction in `n` is cut off.
 *
 * `null` when `date` is not a date or the result leaves Excel's range.
 */
export function workday(date, n) {
    const start = isoToDays(date);
    if (start === null || !Number.isFinite(n))
        return null;
    let remaining = Math.trunc(n);
    if (remaining === 0)
        return daysToIso(start);
    if (Math.abs(remaining) > MAX_WORKDAYS)
        return null;
    const step = remaining > 0 ? 1 : -1;
    let day = start;
    // From a weekend, counting starts at the working day just behind us in the direction we move.
    while (isWeekend(day))
        day -= step;
    const weeks = Math.trunc(remaining / 5);
    day += weeks * 7;
    remaining -= weeks * 5;
    while (remaining !== 0) {
        day += step;
        if (!isWeekend(day))
            remaining -= step;
    }
    return daysToIso(day);
}
/** Working days in the closed range from `from` to `to`, with `from` on or before `to`. */
function countWorkdays(from, to) {
    const total = to - from + 1;
    const weeks = Math.floor(total / 7);
    let count = weeks * 5;
    for (let day = from + weeks * 7; day <= to; day += 1) {
        if (!isWeekend(day))
            count += 1;
    }
    return count;
}
/**
 * Excel `NETWORKDAYS(a, b)` without a holiday list: the working days (Monday to Friday) from `a`
 * to `b`, both counted. Negative when `b` is before `a`. `null` when either is not a date.
 */
export function networkdays(a, b) {
    const from = isoToDays(a);
    const to = isoToDays(b);
    if (from === null || to === null)
        return null;
    if (to >= from)
        return countWorkdays(from, to);
    const count = countWorkdays(to, from);
    return count === 0 ? 0 : -count;
}
//# sourceMappingURL=dates.js.map